import React, { useEffect, useState } from "react";
import { ArrowDown } from "lucide-react";

interface ScrollToBottomButtonProps {
  targetRef: React.RefObject<HTMLElement>;
  containerRef?: React.RefObject<HTMLElement>;
  dependency?: readonly unknown[];
}

const ScrollToBottomButton: React.FC<ScrollToBottomButtonProps> = ({
  targetRef,
  containerRef,
  dependency,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const checkVisibility = () => {
    const container = containerRef?.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Check if we're near the bottom (within 100px threshold)
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 100;

    let isTargetVisible = false;
    if (targetRef?.current) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = targetRef.current.getBoundingClientRect();

      // Target is "reachable" if its top is within or above the viewport
      // (i.e. the user has already scrolled to or past it — no need to scroll down)
      isTargetVisible = targetRect.top <= containerRect.bottom;
    }

    setIsVisible(!isNearBottom && !isTargetVisible);
  };

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    // Check initial visibility
    checkVisibility();

    // Add scroll listener
    container.addEventListener("scroll", checkVisibility);

    return () => {
      container.removeEventListener("scroll", checkVisibility);
    };
  }, [containerRef]);

  // Re-check visibility when dependency (messages) changes
  useEffect(() => {
    checkVisibility();
  }, [dependency]);

  const scrollToBottom = () => {
    targetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToBottom}
      className="absolute bottom-4 left-1/2 -translate-x-1/2 p-2 bg-neutral-white text-neutral-200 border border-border rounded-full shadow-lg animate-bounce z-50 hover:bg-neutral-700 transition-colors flex items-center justify-center"
      aria-label="Scroll to bottom"
    >
      <ArrowDown size={20} />
    </button>
  );
};

export default ScrollToBottomButton;
